import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { LocalizedText, lt, useFeatureFlag } from '@mews/framework';
import { Button, Container, IconName, InputValue, Stack, StackItem, withBreakpointRange } from '@optimus-web/core';
import { isDesktopOrLargeDesktop, isMobileOrSmallPhone, WithBreakpointRange } from '@mews-commander/ui';
import { DateStringPicker } from '@mews-commander/legacy-form';
import { Resource } from '@mews-commander/resources';
import { useCommanderSelector } from '@mews-commander/utils';
import { getTimelineResourceOptimizationEnabled } from '../../selector';
import { NavigationDirection } from '../../types';
import { DensitySelect } from './DensitySelect';
import { TimeUnitSelect } from './TimeUnitSelect';
import { ResourceSearch } from './ResourceSearch';
import { TimelinePagination, TimelinePaginationProps } from './TimelinePagination';

interface TimelineControlBarProps extends WithBreakpointRange, TimelinePaginationProps {
    serviceId: string;
    isOptimizeSpaceAllocationDisabled?: boolean;
    resourcesToHighlight: Resource[];
    searchedSpace: Resource | null;
    loading: boolean;
    onOptimizeSpaceAllocationClick: () => void;
    onDateChange: (utc: string) => void;
    onResourceSearch: (searchValue: InputValue) => void;
    navigateHighlightedResources: (direction: NavigationDirection) => void;
}

export const TimelineControlBar: React.FC<TimelineControlBarProps> = ({
    serviceId,
    breakpointRange,
    isOptimizeSpaceAllocationDisabled,
    resourcesToHighlight,
    searchedSpace,
    loading,
    onOptimizeSpaceAllocationClick,
    onDateChange,
    onNext,
    onNextFast,
    onPrev,
    onPrevFast,
    onToday,
    onResourceSearch,
    navigateHighlightedResources,
}) => {
    const isTimelineSpaceSearchNavigationEnabled = useFeatureFlag(FeatureFlag.timelineSpaceSearchNavigationEnabled);
    const isSpaceOptimizationEnabled = useCommanderSelector(getTimelineResourceOptimizationEnabled);

    const isMobileDevice = isMobileOrSmallPhone(breakpointRange);
    const isDesktopDevice = isDesktopOrLargeDesktop(breakpointRange);

    return (
        <Container width="100%">
            <Stack distribution="space-between" spacing="200">
                <Stack spacing="200">
                    {!isMobileDevice && (
                        <>
                            <StackItem maxWidth="250px" minWidth="120px">
                                <ResourceSearch
                                    onChange={onResourceSearch}
                                    navigateHighlightedResources={navigateHighlightedResources}
                                    resourcesToHighlight={resourcesToHighlight}
                                    searchedSpace={searchedSpace}
                                    isTimelineSpaceSearchNavigationEnabled={isTimelineSpaceSearchNavigationEnabled}
                                />
                            </StackItem>

                            {isDesktopDevice && (
                                <TimelinePagination
                                    onPrevFast={onPrevFast}
                                    onPrev={onPrev}
                                    onToday={onToday}
                                    onNext={onNext}
                                    onNextFast={onNextFast}
                                />
                            )}
                        </>
                    )}

                    <StackItem maxWidth="250px" minWidth="120px">
                        <DateStringPicker
                            showMonthSelector
                            placeholder={lt('SelectDate')}
                            onChange={onDateChange}
                            size="medium"
                        />
                    </StackItem>
                </Stack>
                {!isMobileDevice && (
                    <Stack spacing="100">
                        {isDesktopDevice && isSpaceOptimizationEnabled && (
                            <Button
                                icon={IconName.CalendarStars}
                                onClick={onOptimizeSpaceAllocationClick}
                                disabled={isOptimizeSpaceAllocationDisabled}
                                type="submit"
                                size="medium"
                            >
                                <LocalizedText textKey={TextKey.OptimizeSpaceAllocation} />
                            </Button>
                        )}

                        <TimeUnitSelect disabled={loading} />

                        <DensitySelect serviceId={serviceId} />
                    </Stack>
                )}
            </Stack>
        </Container>
    );
};

export default withBreakpointRange(TimelineControlBar);
